const JOINING_SCRIPT = /[\u0590-\u08FF\uFB1D-\uFDFF\uFE70-\uFEFF]/;

export type SplitHeading = {
  parts: HTMLElement[];
  byWord: boolean;
  revert: () => void;
};

function graphemes(text: string) {
  const Segmenter = Intl.Segmenter;
  return Segmenter ? Array.from(new Segmenter(undefined, { granularity: 'grapheme' }).segment(text), (part) => part.segment) : Array.from(text);
}

function inlineBlock(document: Document, text?: string) {
  const span = document.createElement('span');
  span.style.display = 'inline-block';
  if (text !== undefined) span.textContent = text;
  return span;
}

/**
 * Splits a heading's text into animatable inline-block spans without touching
 * the elements React owns: each text node is swapped for an aria-hidden wrapper
 * and swapped back by `revert`. Joining scripts (Arabic, Hebrew) split by word
 * so letter shaping is preserved.
 */
export function splitHeadingLetters(heading: HTMLElement): SplitHeading | null {
  const document = heading.ownerDocument;
  const label = heading.textContent?.replace(/\s+/g, ' ').trim();
  if (!label) return null;

  const byWord = JOINING_SCRIPT.test(label);
  const walker = document.createTreeWalker(heading, 4 /* NodeFilter.SHOW_TEXT */);
  const texts: Text[] = [];
  while (walker.nextNode()) {
    const node = walker.currentNode as Text;
    if (node.nodeValue?.trim()) texts.push(node);
  }

  const parts: HTMLElement[] = [];
  const swaps = texts.map((text) => {
    const wrapper = document.createElement('span');
    wrapper.setAttribute('aria-hidden', 'true');
    for (const token of (text.nodeValue ?? '').split(/(\s+)/)) {
      if (!token) continue;
      if (!token.trim()) {
        wrapper.append(token);
        continue;
      }
      const word = inlineBlock(document);
      word.style.whiteSpace = 'nowrap';
      if (byWord) {
        word.textContent = token;
        parts.push(word);
      } else {
        for (const letter of graphemes(token)) {
          const span = inlineBlock(document, letter);
          word.append(span);
          parts.push(span);
        }
      }
      wrapper.append(word);
    }
    text.replaceWith(wrapper);
    return { text, wrapper };
  });

  const previousLabel = heading.getAttribute('aria-label');
  heading.setAttribute('aria-label', previousLabel ?? label);

  return {
    parts,
    byWord,
    revert: () => {
      for (const { text, wrapper } of swaps) if (wrapper.isConnected) wrapper.replaceWith(text);
      if (previousLabel === null) heading.removeAttribute('aria-label');
    },
  };
}
